import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import "./index.css";

import color1 from "../assets/Mes2x-color-1.webp";
import color2 from "../assets/Mes2x-color-2.webp";
import color3 from "../assets/Mes2x-color-3.webp";
import shadowImg from "../assets/Mes2x-shadow.webp";

const COLOR_IMAGES = [color1, color2, color3];
const COLOR_LABELS = ["Purple lighting", "Green lighting", "Red lighting"];
const COLOR_INTERVAL_MS = 2000;

export default function App() {
  const rootRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const inView = useInView(rootRef, { margin: "-10% 0px", once: true });
  const shouldAnimate = inView && !prefersReducedMotion;
  const [colorState, setColorState] = useState({ current: 0, prev: 0 });

  useEffect(() => {
    const id = setInterval(
      () =>
        setColorState((s) => ({
          prev: s.current,
          current: (s.current + 1) % COLOR_IMAGES.length
        })),
      COLOR_INTERVAL_MS
    );
    return () => clearInterval(id);
  }, []);

  const fade = (delay = 0) => ({
    initial: { opacity: 0, y: 18 },
    animate: shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 },
    transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }
  });

  const { current: ci, prev: pi } = colorState;

  return (
    <section ref={rootRef} className="br">
      <div className="br-inner">
        <div className="br-header">
          <motion.span className="br-eyebrow" {...fade(0)}>
            <span className="br-eyebrow-mark" />
            Why ArsenalPC
          </motion.span>

          <motion.h2 className="br-title" {...fade(0.1)}>
            Faster Model{" "}
            <span className="br-title-accent">Training, Lower Inference Latency</span>
          </motion.h2>
        </div>

        <div className="br-body-wrap">
          <motion.p className="br-body br-body-lead" {...fade(0.2)}>
            From independent researchers to enterprise ML teams, ArsenalPC
            AI workstations power work that can&apos;t wait for cloud queues or
            tolerate thermal throttling.
          </motion.p>

          <motion.p className="br-body" {...fade(0.3)}>
            We build every workstation for sustained GPU utilization under real
            training loads, not burst gaming workloads. Your multi-day
            experiments complete on schedule, at full speed, without the
            temperature induced slowdowns that force cloud providers to throttle.
          </motion.p>
        </div>

        <motion.div
          className="br-visual"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={
            shouldAnimate ? { opacity: 1, scale: 1 } : { opacity: 1, scale: 1 }
          }
          transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.div
            className="br-callout"
            initial={{ opacity: 0, y: 12 }}
            animate={shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="br-callout-label">
              <span className="br-callout-dot" />
              Sustained Performance
            </span>
            <p className="br-callout-text">
              Full-clock GPUs under <strong>24/7 training loads</strong> with
              zero thermal throttling and no queue wait times.
            </p>
          </motion.div>

          <div className="br-image-frame">
            <img
              className="br-shadow-image"
              src={shadowImg}
              alt=""
              aria-hidden="true"
            />
            {COLOR_IMAGES.map((src, i) => {
              const isCurrent = i === ci;
              const isPrev = i === pi;
              return (
                <img
                  key={i}
                  className="br-color-image"
                  src={src}
                  alt={`ArsenalPC MES2X workstation — ${COLOR_LABELS[i]}`}
                  style={{
                    opacity: isCurrent || isPrev ? 1 : 0,
                    zIndex: isCurrent ? 4 : isPrev ? 3 : 1
                  }}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
